import { useEffect } from 'react';
import { MarketingPageShell } from '@/components/marketing/MarketingPageShell';
import { RedesignButton } from '@/components/marketing/redesign/RedesignButton';
import { RdSection } from '@/components/marketing/redesign/RedesignMotion';
import { PRIMARY_SIGNUP_CTA, SCHEDULE_CALL_CTA, VESTA_SALES_CALENDAR } from '@/lib/marketing-cta';

const KPI_TILES = [
  { label: 'Today RevPAR', value: '$184.20', delta: '+4.2% vs yesterday', up: true },
  { label: 'Today ADR', value: '$246.00', delta: '+1.8%', up: true },
  { label: 'Occupancy', value: '74.9%', delta: '−2.1pp', up: false },
  { label: 'GOPPAR', value: '$92.40', delta: '+6.4%', up: true },
] as const;

const ANOMALIES = [
  {
    tone: 'red',
    title: 'Labor +11% above budget',
    body: 'Tuesday housekeeping overlapped with banquet shift. Check schedule grid.',
    icon: '!',
  },
  {
    tone: 'amber',
    title: 'Booking.com commission creep (20.8%)',
    body: 'Up 1.4pp this week. Direct push or pricing rebalance?',
    icon: '⌃',
  },
  {
    tone: 'green',
    title: 'F&B beat plan by $1.4k',
    body: 'Friday banquet exceeded forecast — flow-through 38%.',
    icon: '✓',
  },
] as const;

const INTEGRATIONS = [
  { name: 'Mews', status: '● Live · PMS sync', live: true, logo: null },
  { name: null, status: '● Live · Cash runway', live: true, logo: '/marketing/quickbooks-logo.png' },
  { name: 'CSV import', status: '● Live · Manual metrics', live: true, logo: null },
  { name: 'Cloudbeds', status: '● Live · PMS sync', live: true, logo: null },
  { name: 'Oracle Opera', status: '○ Roadmap', live: false, logo: null },
  { name: null, status: '○ Roadmap', live: false, logo: '/marketing/xero-logo.png' },
] as const;

const anomalyToneStyle = {
  red: { bg: 'rgba(220,61,61,.12)', color: '#DC3D3D' },
  amber: { bg: 'rgba(237,156,26,.15)', color: '#ED9C1A' },
  green: { bg: 'rgba(31,169,77,.12)', color: '#1FA94D' },
} as const;

const Features = () => {
  useEffect(() => {
    document.title = 'Features · Vesta CFO';
  }, []);

  return (
    <MarketingPageShell navActive="product">
      <RdSection tight hero style={{ paddingTop: 80, paddingBottom: 60 }}>
        <div className="rd-container">
          <span className="hero-eye">
            <span className="pulse" />
            Platform
          </span>
          <h1 className="hero-h" style={{ margin: '18px 0 0', fontSize: 'clamp(40px, 5vw, 64px)', maxWidth: 780 }}>
            Every KPI a hotel cares about
            <br />
            <em>— under one roof.</em>
          </h1>
          <p className="hero-sub" style={{ maxWidth: 580 }}>
            Live dashboards, daily briefings, anomaly detection, and forecasting — all wired to your PMS and finance
            stack.
          </p>
          <div className="feat-jump">
            <a href="#f-kpi">KPI dashboard</a>
            <a href="#f-brief">AI briefings</a>
            <a href="#f-anom">Anomaly detection</a>
            <a href="#f-int">Integrations</a>
            <a href="#f-fc">Forecasting</a>
            <a href="#f-sec">Security</a>
          </div>
        </div>
      </RdSection>

      <RdSection style={{ paddingTop: 40 }}>
        <div className="rd-container">
          <div className="spotlight" id="f-kpi">
            <div>
              <div className="spot-eye">— KPI dashboard</div>
              <h2 className="spot-h">
                RevPAR, ADR, GOPPAR
                <br />— all in one view.
              </h2>
              <p className="spot-b">
                Live tiles for every hotel metric, with day-over-day, week-over-week, and budget deltas. Color-coded to
                your operating thresholds.
              </p>
              <ul className="spot-list">
                <li>10+ hotel KPIs, tabular numerals, no charts-for-the-sake-of-charts</li>
                <li>Channel mix and OTA commission breakdown</li>
                <li>Property snapshot: keys, currency, PMS, timezone</li>
                <li>Switch properties in one click</li>
              </ul>
            </div>
            <div className="spot-img" style={{ padding: 24, background: '#FAF8F2' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {KPI_TILES.map((tile) => (
                  <div
                    key={tile.label}
                    style={{
                      background: '#fff',
                      border: '1px solid var(--rd-border)',
                      borderRadius: 10,
                      padding: '14px 16px',
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        color: 'var(--rd-text-3)',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '.15em',
                      }}
                    >
                      {tile.label}
                    </div>
                    <div
                      style={{
                        fontSize: 26,
                        fontWeight: 600,
                        color: 'var(--vesta-navy)',
                        marginTop: 8,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {tile.value}
                    </div>
                    <div
                      style={{
                        fontSize: 11.5,
                        marginTop: 4,
                        color: tile.up ? '#1FA94D' : '#DC3D3D',
                        fontWeight: 600,
                      }}
                    >
                      {tile.delta}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="spotlight rev" id="f-brief">
            <div className="spot-img">
              <div style={{ background: '#FAF8F2', padding: '24px 28px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div
                      style={{
                        fontSize: 10.5,
                        fontWeight: 600,
                        letterSpacing: '.2em',
                        textTransform: 'uppercase',
                        color: '#C8963E',
                      }}
                    >
                      Briefing · 06:45
                    </div>
                    <h3
                      style={{
                        fontFamily: 'var(--font-display)',
                        fontWeight: 400,
                        fontSize: 22,
                        color: 'var(--vesta-navy)',
                        margin: '6px 0 0',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      Strong weekend, OTA mix shifting
                    </h3>
                  </div>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--rd-text-3)' }}>
                    Daily · in-app + email
                  </span>
                </div>
                <p style={{ fontSize: 14, lineHeight: 1.65, color: 'var(--rd-text-2)', margin: '14px 0 0' }}>
                  RevPAR closed the week at $176.40 (+3.1% wow). Booking.com share rose 4pp at the expense of Direct —
                  net commission cost +$640 vs plan. Consider tightening direct rate parity for next week.
                </p>
                <div
                  style={{
                    marginTop: 14,
                    paddingTop: 14,
                    borderTop: '1px solid var(--rd-border-2)',
                    display: 'flex',
                    gap: 6,
                    flexWrap: 'wrap',
                  }}
                >
                  {['RevPAR', 'channel_mix', 'otas.bcom'].map((tag) => (
                    <span
                      key={tag}
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        padding: '4px 9px',
                        background: '#fff',
                        border: '1px solid var(--rd-border)',
                        borderRadius: 4,
                        color: 'var(--vesta-navy-muted)',
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <div className="spot-eye">— AI briefings</div>
              <h2 className="spot-h">
                A morning brief that
                <br />
                reads like a GM wrote it.
              </h2>
              <p className="spot-b">
                Plain-English summaries delivered to in-app and (optionally) the GM&apos;s inbox. Every claim is cited to
                a dataset — never &quot;AI said so.&quot;
              </p>
              <ul className="spot-list">
                <li>Auto-generated each morning at your timezone</li>
                <li>Cited sources: PMS, payroll, OTAs, vendor spend</li>
                <li>&quot;Ask follow-up&quot; loops back into your data</li>
                <li>Property-scoped tone (boutique vs portfolio)</li>
              </ul>
            </div>
          </div>

          <div className="spotlight" id="f-anom">
            <div>
              <div className="spot-eye">— Anomaly detection</div>
              <h2 className="spot-h">
                Find leakage
                <br />
                before month-end.
              </h2>
              <p className="spot-b">
                Vesta continuously compares actuals against budget, last-year, and your operating ranges — and flags only
                what matters.
              </p>
              <ul className="spot-list">
                <li>Labor, F&amp;B, OTA commission, vendor invoice anomalies</li>
                <li>Severity: green / amber / red, configurable per metric</li>
                <li>Snooze, escalate to GM, or push to Slack</li>
                <li>Cited transactions, not opaque scores</li>
              </ul>
            </div>
            <div className="spot-img" style={{ padding: 18, background: '#FAF8F2' }}>
              {ANOMALIES.map((item) => (
                <div
                  key={item.title}
                  style={{
                    background: '#fff',
                    border: '1px solid var(--rd-border)',
                    borderRadius: 10,
                    padding: '12px 14px',
                    marginBottom: 10,
                    display: 'flex',
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 7,
                      background: anomalyToneStyle[item.tone].bg,
                      color: anomalyToneStyle[item.tone].color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: 'none',
                    }}
                  >
                    {item.icon}
                  </div>
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--vesta-navy)' }}>{item.title}</div>
                    <div style={{ fontSize: 12.5, color: 'var(--rd-text-2)', marginTop: 3, lineHeight: 1.5 }}>
                      {item.body}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="spotlight rev" id="f-int">
            <div className="spot-img" style={{ padding: 24, background: '#FAF8F2' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                {INTEGRATIONS.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      background: '#fff',
                      border: '1px solid var(--rd-border)',
                      borderRadius: 10,
                      padding: 14,
                      textAlign: 'center',
                    }}
                  >
                    {item.logo ? (
                      <img src={item.logo} style={{ height: 24, margin: '0 auto', objectFit: 'contain' }} alt="" />
                    ) : (
                      <div
                        style={{
                          fontFamily: 'var(--font-display)',
                          fontSize: item.name === 'Mews' ? 22 : 18,
                          fontWeight: 600,
                          color: 'var(--vesta-navy)',
                        }}
                      >
                        {item.name}
                      </div>
                    )}
                    <div
                      style={{
                        fontSize: 11,
                        color: item.live ? '#1FA94D' : 'var(--rd-text-3)',
                        fontWeight: 600,
                        marginTop: 6,
                      }}
                    >
                      {item.status}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="spot-eye">— Integrations</div>
              <h2 className="spot-h">
                Plug into your stack.
                <br />
                Or just bring a CSV.
              </h2>
              <p className="spot-b">
                Mews, Cloudbeds, and QuickBooks are live today. CSV always works. Opera, STR, and more accounting
                connectors expand on the roadmap.
              </p>
              <ul className="spot-list">
                <li>Mews PMS — live sync, no middleware</li>
                <li>Cloudbeds — live sync for daily metrics</li>
                <li>QuickBooks — bank balances, vendor spend, runway</li>
                <li>CSV import — daily metrics, channel, comp, events</li>
              </ul>
            </div>
          </div>

          <div
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginTop: 120 }}
            id="f-fc"
          >
            <div className="step" style={{ padding: 32 }} id="f-sec">
              <div className="step-i">↗</div>
              <h3 style={{ fontSize: 22 }}>Forecasting · 90-day outlook</h3>
              <p style={{ fontSize: 14.5 }}>
                Project RevPAR, GOP, and cash runway forward using your booking pace, historicals, and budget targets.
                Re-runs every night.
              </p>
            </div>
            <div className="step" style={{ padding: 32 }}>
              <div className="step-i">◆</div>
              <h3 style={{ fontSize: 22 }}>Security · enterprise-ready</h3>
              <p style={{ fontSize: 14.5 }}>
                256-bit encryption in transit and at rest, tenant isolation, SSO-ready, signed DPAs for Enterprise. Your
                data never sold or shared.
              </p>
            </div>
          </div>

          <div style={{ marginTop: 96 }}>
            <div className="cta-block">
              <div className="h">
                See it on <em>your own data.</em>
              </div>
              <p>Connect Mews or upload a CSV — 14-day free trial, no card required.</p>
              <div className="row">
                <RedesignButton variant="gold" size="lg" to="/auth">
                  {PRIMARY_SIGNUP_CTA}
                </RedesignButton>
                <RedesignButton variant="outline-light" size="lg" href={VESTA_SALES_CALENDAR} external>
                  {SCHEDULE_CALL_CTA}
                </RedesignButton>
              </div>
            </div>
          </div>
        </div>
      </RdSection>
    </MarketingPageShell>
  );
};

export default Features;
